import { create, fromBinary, toBinary } from '@bufbuild/protobuf';
import { msg } from './messages';
import {
  ClientMessageSchema,
  ErrorCode,
  ServerMessageSchema,
  type ClientMessage,
  type ErrorMessage,
  type ServerMessage,
} from './gen/ddtank/match/v1/match_pb';

export const SUB_PROTOCOL = 'ddtank.match.v1';
export const BEARER_PREFIX = 'bearer.';
/** Código de fechamento 1013 ("tente de novo mais tarde"): o servidor manda quando a partida está em outra instância. */
export const CLOSE_TRY_AGAIN_LATER = 1013;

export type ClientPayload = NonNullable<ClientMessage['payload']>;
export type ServerPayload = NonNullable<ServerMessage['payload']>;
export type ServerPayloadCase = ServerPayload['case'];

/** Extrai o valor de um caso do `oneof` do servidor: `PayloadOf<'room'>` é o `RoomState`. */
export type PayloadOf<C extends ServerPayloadCase> = Extract<ServerPayload, { case: C }>['value'];

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface MinimalWebSocket {
  binaryType: string;
  readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: ArrayBufferLike | ArrayBufferView): void;
  close(code?: number, reason?: string): void;
}

export type WebSocketFactory = (url: string, protocols: string[]) => MinimalWebSocket;

export interface MatchClientOptions {
  /** Endereço do WebSocket, sem query (`ws://localhost:5000/api/match/ws`). */
  url: string;
  /** Token de acesso atual; chamado a cada (re)conexão, então um refresh entre quedas é aproveitado. */
  getToken: () => string | Promise<string>;
  webSocketFactory?: WebSocketFactory;
  /** Reconectar sozinho depois de uma queda (padrão: sim). */
  reconnect?: boolean;
  initialDelayMs?: number;
  maxDelayMs?: number;
  /** Intervalo do ping de manutenção. 0 desliga. */
  pingIntervalMs?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  now?: () => number;
}

export class MatchRequestError extends Error {
  constructor(
    public readonly code: ErrorCode,
    public readonly detail: string,
  ) {
    super(`${ErrorCode[code] ?? code}${detail ? `: ${detail}` : ''}`);
    this.name = 'MatchRequestError';
  }
}

type Listener<T> = (value: T) => void;

class Emitter<T> {
  private readonly listeners = new Set<Listener<T>>();

  on(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(value: T): void {
    for (const listener of [...this.listeners]) listener(value);
  }
}

interface Pending {
  resolve: (message: ServerMessage) => void;
  reject: (error: Error) => void;
  timer: unknown;
}

/**
 * Cliente do WebSocket do Match: envia `ClientMessage`, entrega `ServerMessage`, casa respostas por `requestId`, reconecta com recuo
 * exponencial (renovando o token a cada tentativa) e segue a dica de afinidade do servidor (`?room=<id>`, ADR 0010).
 */
export class MatchClient {
  private socket: MinimalWebSocket | null = null;
  private stateValue: ConnectionState = 'idle';
  private attempt = 0;
  private roomHint = '';
  private nextRequest = 1;
  private reconnectTimer: unknown = null;
  private pingTimer: unknown = null;
  private wantOpen = false;
  private readonly pending = new Map<string, Pending>();
  private readonly messageEmitter = new Emitter<ServerMessage>();
  private readonly stateEmitter = new Emitter<ConnectionState>();
  private readonly failureEmitter = new Emitter<Error>();
  private readonly options: Required<Omit<MatchClientOptions, 'webSocketFactory' | 'setTimer' | 'clearTimer' | 'now'>> &
    Pick<MatchClientOptions, 'webSocketFactory' | 'setTimer' | 'clearTimer' | 'now'>;

  constructor(options: MatchClientOptions) {
    this.options = {
      reconnect: true,
      initialDelayMs: 500,
      maxDelayMs: 8000,
      pingIntervalMs: 15000,
      ...options,
    };
  }

  get state(): ConnectionState {
    return this.stateValue;
  }

  /** Última sala conhecida do jogador; vai como dica na reconexão. */
  get activeRoomId(): string {
    return this.roomHint;
  }

  onMessage(listener: Listener<ServerMessage>): () => void {
    return this.messageEmitter.on(listener);
  }

  onState(listener: Listener<ConnectionState>): () => void {
    return this.stateEmitter.on(listener);
  }

  /** Falhas de conexão (recusa do upgrade, queda). Não substitui `onState`: serve para mostrar o motivo. */
  onFailure(listener: Listener<Error>): () => void {
    return this.failureEmitter.on(listener);
  }

  connect(): void {
    if (this.wantOpen) return;
    this.wantOpen = true;
    this.attempt = 0;
    void this.open();
  }

  close(): void {
    this.wantOpen = false;
    this.clearReconnect();
    this.stopPing();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      try {
        socket.close(1000, 'cliente encerrou');
      } catch {
        // já fechado
      }
    }
    this.rejectAll(new Error('Conexão encerrada.'));
    this.setState('closed');
  }

  /** Envia sem esperar resposta. Falso se a conexão não está aberta. */
  send(payload: ClientPayload, requestId = ''): boolean {
    const socket = this.socket;
    if (!socket || socket.readyState !== 1) return false;
    const message = create(ClientMessageSchema, { requestId, payload });
    socket.send(toBinary(ClientMessageSchema, message));
    return true;
  }

  /**
   * Envia e espera a resposta com o mesmo `requestId`. Um `error` do servidor vira `MatchRequestError`.
   * Nem toda mensagem tem resposta direta: use só para pedidos que o servidor responde (lista, sala, fila, ping).
   */
  request(payload: ClientPayload, timeoutMs = 5000): Promise<ServerMessage> {
    const requestId = `r${this.nextRequest++}`;
    return new Promise<ServerMessage>((resolve, reject) => {
      const timer = this.timers.set(() => {
        this.pending.delete(requestId);
        reject(new Error('O servidor não respondeu a tempo.'));
      }, timeoutMs);
      this.pending.set(requestId, { resolve, reject, timer });
      if (!this.send(payload, requestId)) {
        this.timers.clear(timer);
        this.pending.delete(requestId);
        reject(new Error('Sem conexão com o servidor.'));
      }
    });
  }

  private readonly timers = {
    set: (fn: () => void, ms: number): unknown => (this.options.setTimer ?? ((f, t) => setTimeout(f, t)))(fn, ms),
    clear: (handle: unknown): void => (this.options.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>)))(handle),
  };

  private async open(): Promise<void> {
    if (!this.wantOpen) return;
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
    let token: string;
    try {
      token = await this.options.getToken();
    } catch (error) {
      this.failureEmitter.emit(error instanceof Error ? error : new Error(String(error)));
      this.scheduleReconnect();
      return;
    }
    if (!this.wantOpen) return;

    const url = this.roomHint ? `${this.options.url}?room=${encodeURIComponent(this.roomHint)}` : this.options.url;
    const factory = this.options.webSocketFactory ?? ((u, p) => new WebSocket(u, p) as unknown as MinimalWebSocket);
    const socket = factory(url, [SUB_PROTOCOL, BEARER_PREFIX + token]);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.attempt = 0;
      this.setState('open');
      // Primeira medição do relógio do servidor logo ao abrir (o jogo precisa dela para o cronômetro do turno).
      this.send(msg.ping((this.options.now ?? Date.now)()));
      this.startPing();
    };
    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      this.handleFrame(event.data);
    };
    socket.onerror = () => {
      // O detalhe vem no onclose; navegadores não expõem o motivo do erro.
    };
    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.stopPing();
      this.rejectAll(new Error('Conexão perdida.'));
      this.failureEmitter.emit(new Error(`Conexão encerrada (${event.code}${event.reason ? `: ${event.reason}` : ''}).`));
      if (this.wantOpen && this.options.reconnect) {
        // 1013: a partida mora em outra instância; a dica de sala já foi guardada, reconecta sem esperar.
        this.scheduleReconnect(event.code === CLOSE_TRY_AGAIN_LATER);
      } else {
        this.wantOpen = false;
        this.setState('closed');
      }
    };
  }

  private handleFrame(data: unknown): void {
    let message: ServerMessage;
    try {
      const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data instanceof Uint8Array ? data : null;
      if (!bytes) return;
      message = fromBinary(ServerMessageSchema, bytes);
    } catch {
      return; // quadro corrompido: ignora, o servidor é a fonte da verdade
    }

    const payload = message.payload;
    if (payload.case === 'welcome') {
      this.roomHint = payload.value.activeRoomId;
    } else if (payload.case === 'room') {
      this.roomHint = payload.value.roomId;
    } else if (payload.case === 'roomClosed') {
      this.roomHint = '';
    } else if (payload.case === 'error' && payload.value.code === ErrorCode.WRONG_INSTANCE) {
      this.roomHint = payload.value.detail;
    }

    const pending = message.requestId ? this.pending.get(message.requestId) : undefined;
    if (pending) {
      this.pending.delete(message.requestId);
      this.timers.clear(pending.timer);
      if (payload.case === 'error') {
        pending.reject(new MatchRequestError(payload.value.code, payload.value.detail));
      } else {
        pending.resolve(message);
      }
    }

    this.messageEmitter.emit(message);
  }

  private scheduleReconnect(immediate = false): void {
    if (!this.wantOpen || !this.options.reconnect) return;
    this.setState('reconnecting');
    const delay = immediate ? 0 : Math.min(this.options.maxDelayMs, this.options.initialDelayMs * 2 ** this.attempt);
    this.attempt++;
    this.clearReconnect();
    this.reconnectTimer = this.timers.set(() => void this.open(), delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      this.timers.clear(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startPing(): void {
    this.stopPing();
    if (this.options.pingIntervalMs <= 0) return;
    const tick = () => {
      this.send(msg.ping((this.options.now ?? Date.now)()));
      this.pingTimer = this.timers.set(tick, this.options.pingIntervalMs);
    };
    this.pingTimer = this.timers.set(tick, this.options.pingIntervalMs);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      this.timers.clear(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private rejectAll(error: Error): void {
    for (const [id, pending] of this.pending) {
      this.timers.clear(pending.timer);
      pending.reject(error);
      this.pending.delete(id);
    }
  }

  private setState(state: ConnectionState): void {
    if (this.stateValue === state) return;
    this.stateValue = state;
    this.stateEmitter.emit(state);
  }
}

export function isError(message: ServerMessage): message is ServerMessage & { payload: { case: 'error'; value: ErrorMessage } } {
  return message.payload.case === 'error';
}
